import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  notificationTemplateCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { notificationEndpoints } from "@/features/notification/api/endpoints";
import type { NotificationTemplateDto } from "@/features/notification/api/dto";

/**
 * Crée ou remplace un modèle de notification
 * (`POST /NotificationTemplates/add` — guide §6.7). Le corps accepte des
 * variables substituées à l'envoi. Rôles : flashpay-superadmin, flashpay-admin.
 */
export const useCreateNotificationTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["notification", "templates", "create"],
    mutationFn: (body: NotificationTemplateDto) =>
      mutateData({
        url: notificationEndpoints.templates.add,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<NotificationTemplateDto>,
    onSuccess: (_data, body) => {
      return invalidateAfterMutation(
        queryClient,
        notificationTemplateCacheKeys(body.code, body.channel, body.language),
        { resetListPage: true }
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreateNotificationTemplate failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "notification" }
      );
    },
  });
};
